/**
 * The theme this browser saw last, so the first paint is not a guess.
 *
 * ## The flicker it exists to remove
 *
 * `usePersistentWorkspace` starts the reducer on `makeDemoWorkspace()`, whose
 * `themeRef` is Classic, and the real one only arrives when
 * `store.load` answers. Between those two moments the whole shell is painted in
 * a theme nobody chose — for exactly as long as that round trip takes, which on
 * a cold backend is seconds rather than a frame. The workspace cannot be
 * fetched synchronously; a *guess at one field of it* can be stored where the
 * first render can read it.
 *
 * Same idiom and same reason as `editor-split.ts` and `interval-mru.ts`:
 * `localStorage` is synchronous, so the app starts in the right state instead
 * of correcting itself once.
 *
 * ## The server still decides
 *
 * This is a cache of the last answer, never the answer. `store.load` overwrites
 * it on every boot, so a theme changed in another browser costs one flicker
 * *there* and then agrees. Nothing is ever written to the server from here, and
 * a value that no longer resolves degrades through `themeById` to the default —
 * which is precisely what the unseeded first paint did anyway.
 *
 * ## Keyed by user, and that is not optional
 *
 * An admin acting as `demo2` reads `demo2`'s workspace (see `act-as.ts`), so a
 * single unscoped key would seed the first paint with whichever account was
 * open last — the app would boot in the admin's theme and then switch, which is
 * the bug this file removes, moved rather than fixed.
 *
 * ## What it does not cover
 *
 * An **authored** theme resolves through `themes`, which `loadThemes` fetches
 * separately, so seeding its id still paints the default until that request
 * lands. Only the built-ins are flicker-free. Caching the resolved `Theme`
 * object would close that too, at the cost of storing a palette this module
 * would then have to keep in step with the server's copy.
 */

const PREFIX = "qufolio.theme.ref:";

/**
 * The theme this user was last seen in, or `undefined` for "no idea".
 *
 * `undefined` rather than the default id, so the fallback lives in exactly one
 * place — the demo workspace this seeds. A default repeated here would be a
 * second answer to "what theme does a new browser open in".
 */
export function rememberedThemeRef(userId: string): string | undefined {
  try {
    const raw = window.localStorage.getItem(PREFIX + userId);
    return raw ? raw : undefined;
  } catch {
    // Safari private mode throws on access. Not knowing the theme costs the
    // flicker this module removes; it must never cost the app.
    return undefined;
  }
}

/** Remember it. Called once the load has answered, never before — see the caller. */
export function rememberThemeRef(userId: string, themeRef: string | undefined): void {
  try {
    if (themeRef) window.localStorage.setItem(PREFIX + userId, themeRef);
    // A workspace with no `themeRef` renders the default, and a stale entry
    // would seed the first paint with a theme this user has stopped using.
    else window.localStorage.removeItem(PREFIX + userId);
  } catch {
    // Storage refused. The theme still applies for this session; it simply does
    // not shorten the next boot, which is how the app behaved before this.
  }
}
